import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { User, ShoppingCart, Settings, BookOpen } from 'lucide-react';
import { useApp } from '../context/AppContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const DashboardPage = ({ onAuthComplete }) => {
  const { user, setUser } = useApp();
  const [orders, setOrders] = useState([]);
  const [courseAccess, setCourseAccess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authProcessing, setAuthProcessing] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [notAdminDuringMaintenance, setNotAdminDuringMaintenance] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessedAuth = useRef(false);

  useEffect(() => {
    // Check for auth callback in URL hash
    const hash = window.location.hash;
    const sessionIdMatch = hash.match(/session_id=([^&]+)/);
    
    if (sessionIdMatch && !hasProcessedAuth.current) {
      hasProcessedAuth.current = true;
      setAuthProcessing(true);
      processSession(sessionIdMatch[1]);
    } else if (!user && !location.state?.user && !sessionIdMatch) {
      // Only redirect if there's no session_id in URL (not trying to auth)
      navigate('/');
      return;
    } else if (user) {
      fetchData();
    }
  }, []);

  useEffect(() => {
    if (user && !authProcessing) {
      fetchData();
    }
  }, [user, authProcessing]);

  const processSession = async (sessionId) => {
    try {
      console.log('Processing session with ID:', sessionId);
      const response = await axios.post(`${API}/auth/session`, 
        { session_id: sessionId },
        { withCredentials: true }
      );
      console.log('Auth response:', response.data);
      const authUser = response.data;
      setUser(authUser);
      
      // Notify parent component if callback provided
      if (onAuthComplete) {
        onAuthComplete(authUser);
      }
      
      // Check if this is maintenance mode login attempt
      if (onAuthComplete && !authUser.is_admin) {
        // Non-admin tried to login during maintenance
        setNotAdminDuringMaintenance(true);
        setAuthProcessing(false);
        window.history.replaceState(null, '', window.location.pathname);
        return;
      }
      
      toast.success('Welcome back!');
      // Clear the hash from URL
      window.history.replaceState(null, '', window.location.pathname);
      setAuthProcessing(false);
    } catch (e) {
      console.error('Auth error:', e);
      console.error('Auth error response:', e.response?.data);
      
      // Show session expired screen instead of redirecting
      setSessionExpired(true);
      setAuthProcessing(false);
      // Clear the invalid hash
      window.history.replaceState(null, '', window.location.pathname);
      
      if (onAuthComplete) {
        onAuthComplete(null);
      }
    }
  };

  const handleSignIn = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const fetchData = async () => {
    try {
      const [ordersRes, accessRes] = await Promise.all([
        axios.get(`${API}/orders`, { withCredentials: true }),
        axios.get(`${API}/course/access`, { withCredentials: true }).catch(() => ({ data: { has_access: false } }))
      ]);
      setOrders(ordersRes.data);
      setCourseAccess(accessRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const currentUser = user || location.state?.user;
  const tierLabels = { entry: 'Entry', standard: 'Standard', pro: 'Pro' };
  const tierColors = { 
    entry: 'bg-blue-500/20 text-blue-400 border-blue-500/30', 
    standard: 'bg-[#D4AF37]/20 text-[#D4AF37] border-[#D4AF37]/30', 
    pro: 'bg-[#FFD700]/20 text-[#FFD700] border-[#FFD700]/30' 
  };

  // Show loading while processing auth
  if (authProcessing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-neutral-400">Signing you in...</p>
        </div>
      </div>
    );
  }

  // Show message when non-admin tries to login during maintenance
  if (notAdminDuringMaintenance) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505] px-6">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-yellow-500/10 flex items-center justify-center">
            <Settings size={40} className="text-yellow-500" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-3" style={{ fontFamily: 'Syne' }}>
            Site Under Maintenance
          </h1>
          <p className="text-neutral-400 mb-6">
            The site is currently under maintenance. Only administrators can access it right now. Please check back later.
          </p>
          <button
            onClick={() => window.location.href = '/'}
            className="btn-secondary"
          >
            Go back to home
          </button>
        </div>
      </div>
    );
  }

  // Show session expired / sign in on this device screen
  if (sessionExpired) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505] px-6">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
            <User size={40} className="text-[#D4AF37]" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-3" style={{ fontFamily: 'Syne' }}>
            Sign In Required
          </h1>
          <p className="text-neutral-400 mb-6">
            Your session has expired or this is a new device. Please sign in to continue.
          </p>
          <button
            onClick={handleSignIn}
            className="btn-primary w-full flex items-center justify-center gap-2"
            data-testid="signin-device-btn"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Sign In with Google
          </button>
          <button
            onClick={() => navigate('/')}
            className="mt-4 text-neutral-500 hover:text-[#D4AF37] transition-colors text-sm"
          >
            Go back to home
          </button>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return null;
  }

  return (
    <div className="min-h-screen pt-24 pb-32 px-6 lg:px-12" data-testid="dashboard-page">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-12">
          <div className="flex items-center gap-4">
            {currentUser.picture ? (
              <img src={currentUser.picture} alt="" className="w-16 h-16 rounded-full border-2 border-[#D4AF37]/30" />
            ) : (
              <div className="w-16 h-16 rounded-full bg-[#D4AF37]/20 flex items-center justify-center border border-[#D4AF37]/30">
                <User size={32} className="text-[#D4AF37]" />
              </div>
            )}
            <div>
              <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Syne' }}>
                Hey, <span className="gradient-text">{currentUser.name?.split(' ')[0]}!</span>
              </h1>
              <p className="text-neutral-500">{currentUser.email}</p>
            </div>
          </div>
          {currentUser.is_admin && (
            <Link to="/admin" className="btn-secondary flex items-center gap-2" data-testid="admin-link">
              <Settings size={18} />
              Admin
            </Link>
          )}
        </div>

        {/* Course Access Card */}
        {courseAccess?.has_access && (
          <div className="glass-card p-6 mb-8" data-testid="course-access-card">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#D4AF37] to-[#FFD700] flex items-center justify-center">
                  <BookOpen size={24} className="text-black" />
                </div>
                <div>
                  <h3 className="font-bold text-white" style={{ fontFamily: 'Syne' }}>Mix Course Access</h3>
                  <span className={`px-2 py-0.5 rounded text-xs border ${tierColors[courseAccess.tier]}`}>
                    {tierLabels[courseAccess.tier]} Tier
                  </span>
                </div>
              </div>
              <Link to="/course" className="btn-primary" data-testid="view-course-btn">
                View Course
              </Link>
            </div>
          </div>
        )}

        <h2 className="text-xl font-bold mb-6 text-white" style={{ fontFamily: 'Syne' }}>Your Orders</h2>

        {loading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="glass-card p-6 animate-pulse">
                <div className="h-5 bg-neutral-800 rounded w-1/4 mb-4" />
                <div className="h-4 bg-neutral-800 rounded w-full" />
              </div>
            ))}
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-12">
            <ShoppingCart size={48} className="mx-auto mb-4 text-[#D4AF37]/30" />
            <p className="text-neutral-500 mb-6">You haven't made any purchases yet</p>
            <button onClick={() => navigate('/beats')} className="btn-primary">
              Browse Beats
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map(order => (
              <div key={order.order_id} className="glass-card p-6" data-testid={`order-${order.order_id}`}>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="font-mono text-sm text-neutral-500">{order.order_id}</p>
                    <p className="text-sm text-neutral-400">
                      {new Date(order.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold gradient-text">${order.total?.toFixed(2)} CAD</p>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      order.payment_status === 'paid' 
                        ? 'bg-green-500/20 text-green-500' 
                        : order.payment_status === 'proof_submitted'
                        ? 'bg-blue-500/20 text-blue-400'
                        : 'bg-yellow-500/20 text-yellow-500'
                    }`}>
                      {order.payment_status === 'proof_submitted' ? 'Under Review' : order.payment_status}
                    </span>
                  </div>
                </div>
                <div className="space-y-2">
                  {order.items?.map((item, i) => (
                    <div key={i} className="flex items-center justify-between text-sm py-2 border-t border-[#D4AF37]/10">
                      <span className="text-neutral-300">{item.title}</span>
                      <span className="text-neutral-500">${item.price} CAD</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardPage;
